import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/utils'
import {
  Palette,
  Crown,
  Sparkles,
  ImagePlus,
  FileText,
  Check,
  ExternalLink,
  AlertCircle,
  Image as ImageIcon,
  StickyNote,
  Eye,
} from 'lucide-react'
import {
  useDesignAssets,
  useDesignAssetCounts,
  type DesignAsset,
  type DesignCategory,
} from '@/hooks/useProjectDesignAssets'
import { formatDate } from '@/lib/utils'

// =============================================================================
// Constants
// =============================================================================

interface CategoryConfig {
  key: DesignCategory
  label: string
  description: string
  icon: React.ElementType
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'logo',
    label: 'Logo & Identite',
    description: 'Logo, charte graphique, identite visuelle',
    icon: Crown,
  },
  {
    key: 'palette',
    label: 'Couleurs & Typo',
    description: 'Couleurs preferees, polices, ambiance',
    icon: Palette,
  },
  {
    key: 'inspiration',
    label: 'Inspirations',
    description: 'Sites web, designs, visuels',
    icon: Sparkles,
  },
  {
    key: 'photo',
    label: 'Photos & Medias',
    description: 'Photos equipe, produits, locaux',
    icon: ImagePlus,
  },
  {
    key: 'other',
    label: 'Notes & Details',
    description: 'Notes, consignes, contraintes',
    icon: FileText,
  },
]

const CATEGORY_LABELS: Record<DesignCategory, string> = {
  logo: 'Logo',
  palette: 'Palette',
  inspiration: 'Inspiration',
  mockup: 'Maquette',
  photo: 'Photo',
  typography: 'Typographie',
  other: 'Autre',
}

// =============================================================================
// Helpers
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 o'
  const k = 1024
  const sizes = ['o', 'Ko', 'Mo', 'Go']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// =============================================================================
// Sub-components
// =============================================================================

function CategoryNav({
  activeCategory,
  onCategoryChange,
  assetCounts,
}: {
  activeCategory: DesignCategory
  onCategoryChange: (cat: DesignCategory) => void
  assetCounts: Record<string, number>
}) {
  return (
    <nav aria-label="Categories design" className="flex flex-col gap-2 md:flex-row md:gap-3">
      {CATEGORIES.map((cat) => {
        const count = assetCounts[cat.key] ?? 0
        const isActive = activeCategory === cat.key
        const hasAssets = count > 0
        const Icon = cat.icon

        return (
          <button
            key={cat.key}
            type="button"
            onClick={() => onCategoryChange(cat.key)}
            className={cn(
              'relative flex items-center gap-3 md:flex-col md:items-center md:text-center',
              'flex-1 rounded-xl border-2 p-3 md:p-4',
              'transition-all duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-mint-500 focus-visible:ring-offset-2',
              isActive && 'border-mint-500 bg-white shadow-sm',
              !isActive && hasAssets && 'border-mint-200 bg-mint-50/50 hover:border-mint-400',
              !isActive && !hasAssets && 'border-beige-200 bg-white hover:border-beige-400',
            )}
          >
            {hasAssets && (
              <div className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-mint-500 shadow-sm">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </div>
            )}

            <div className="flex items-center gap-2 md:flex-col md:gap-1">
              <Icon
                className={cn(
                  'h-5 w-5 flex-shrink-0 transition-colors',
                  isActive && 'text-mint-600',
                  !isActive && hasAssets && 'text-mint-500',
                  !isActive && !hasAssets && 'text-brown-400',
                )}
              />
              <span
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive && 'text-mint-800',
                  !isActive && hasAssets && 'text-mint-700',
                  !isActive && !hasAssets && 'text-brown-700',
                )}
              >
                {cat.label}
              </span>
            </div>

            {count > 0 && (
              <Badge variant={isActive ? 'mint' : 'mint'} size="sm" className="md:mt-1">
                {count}
              </Badge>
            )}
          </button>
        )
      })}
    </nav>
  )
}

function ImageGallery({ assets }: { assets: DesignAsset[] }) {
  if (assets.length === 0) return null

  return (
    <div>
      <h4 className="text-sm font-semibold text-brown-800 mb-3 flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-brown-500" />
        Images & fichiers ({assets.length})
      </h4>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {assets.map((asset) => {
          const isPdf = asset.file_type === 'application/pdf'

          return (
            <div
              key={asset.id}
              className="group relative overflow-hidden rounded-xl border border-beige-200 bg-white hover:border-beige-400 hover:shadow-sm transition-all duration-200"
            >
              {isPdf ? (
                <a
                  href={asset.file_url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-40 items-center justify-center bg-beige-50 hover:bg-beige-100 transition-colors"
                >
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="h-10 w-10 text-brown-400" />
                    <span className="text-xs font-medium text-brown-500">PDF</span>
                  </div>
                </a>
              ) : (
                <a
                  href={asset.file_url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative"
                >
                  <img
                    src={asset.file_url || ''}
                    alt={asset.title || asset.file_name || 'Image'}
                    className="h-40 w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                  </div>
                </a>
              )}

              <div className="p-3">
                <p className="truncate text-sm font-medium text-brown-800">
                  {asset.title || asset.file_name || 'Sans titre'}
                </p>
                {asset.description && (
                  <p className="mt-0.5 line-clamp-2 text-xs text-brown-500">
                    {asset.description}
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <Badge variant="beige" size="sm">
                    {CATEGORY_LABELS[asset.category]}
                  </Badge>
                  {asset.file_size && (
                    <span className="text-[10px] text-brown-400">
                      {formatFileSize(asset.file_size)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TextEntries({ assets }: { assets: DesignAsset[] }) {
  if (assets.length === 0) return null

  return (
    <div>
      <h4 className="text-sm font-semibold text-brown-800 mb-3 flex items-center gap-2">
        <StickyNote className="w-4 h-4 text-brown-500" />
        Notes & references ({assets.length})
      </h4>
      <div className="space-y-3">
        {assets.map((asset) => {
          const isColor = asset.type === 'color'
          const isLink = asset.type === 'link'

          return (
            <Card key={asset.id} variant="bordered" padding="none">
              <div className="flex items-start gap-3 p-4">
                <div
                  className={cn(
                    'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                    isColor && 'bg-purple-50',
                    isLink && 'bg-blue-50',
                    !isColor && !isLink && 'bg-beige-100',
                  )}
                >
                  {isColor ? (
                    <Palette className="w-4 h-4 text-purple-500" />
                  ) : isLink ? (
                    <ExternalLink className="w-4 h-4 text-blue-500" />
                  ) : (
                    <FileText className="w-4 h-4 text-brown-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-brown-900 text-sm">
                    {asset.title || 'Sans titre'}
                  </p>

                  <div className="mt-1">
                    {isColor && asset.content && (
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-6 h-6 rounded-full border border-beige-300 flex-shrink-0"
                          style={{ backgroundColor: asset.content }}
                        />
                        <span className="text-sm text-brown-700 font-mono">
                          {asset.content}
                        </span>
                      </div>
                    )}

                    {isLink && asset.content && (
                      <a
                        href={asset.content}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-mint-700 hover:text-mint-800 underline underline-offset-2 break-all"
                      >
                        {asset.content}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    )}

                    {!isColor && !isLink && asset.content && (
                      <p className="text-sm text-brown-700 whitespace-pre-wrap break-words">
                        {asset.content}
                      </p>
                    )}
                  </div>

                  {asset.description && (
                    <p className="mt-1.5 text-xs text-brown-500 leading-relaxed">
                      {asset.description}
                    </p>
                  )}

                  <p className="mt-2 text-xs text-brown-400">
                    {formatDate(asset.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

function EmptyCategory({ category }: { category: CategoryConfig }) {
  const Icon = category.icon

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-beige-200 bg-beige-50/50 px-6 py-12 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-beige-100">
        <Icon className="h-8 w-8 text-brown-400" />
      </div>
      <p className="text-sm font-medium text-brown-700">
        Aucun element dans cette categorie
      </p>
      <p className="mt-1 max-w-xs text-xs text-brown-500">
        Le client n'a pas encore depose d'elements pour «{category.label}».
      </p>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

interface DesignTabProps {
  projectId: string
}

export function DesignTab({ projectId }: DesignTabProps) {
  const [activeCategory, setActiveCategory] = useState<DesignCategory>('logo')
  const { data: allAssets, isLoading, isError, error } = useDesignAssets(projectId)
  const { data: assetCounts } = useDesignAssetCounts(projectId)

  const categoryAssets = useMemo(() => {
    if (!allAssets) return { imageAssets: [] as DesignAsset[], textAssets: [] as DesignAsset[] }

    const filtered = allAssets.filter((a) => a.category === activeCategory)
    return {
      imageAssets: filtered.filter((a) => a.type === 'image'),
      textAssets: filtered.filter((a) => a.type === 'text' || a.type === 'color' || a.type === 'link'),
    }
  }, [allAssets, activeCategory])

  const totalAssets = allAssets?.length ?? 0
  const filledCategories = CATEGORIES.filter((cat) => (assetCounts?.[cat.key] ?? 0) > 0).length
  const activeCategoryConfig = CATEGORIES.find((c) => c.key === activeCategory) || CATEGORIES[0]
  const hasAssetsInCategory = categoryAssets.imageAssets.length > 0 || categoryAssets.textAssets.length > 0

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  // Error
  if (isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <p className="text-sm font-medium text-brown-800 mb-1">
            Impossible de charger les elements design
          </p>
          <p className="text-xs text-brown-500 max-w-sm text-center">
            {error instanceof Error ? error.message : 'Une erreur est survenue.'}
          </p>
        </div>
      </Card>
    )
  }

  // Empty state - no assets at all
  if (totalAssets === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-beige-100">
            <Palette className="h-8 w-8 text-brown-400" />
          </div>
          <p className="text-lg font-semibold text-brown-800 mb-1">
            Aucun element design
          </p>
          <p className="text-sm text-brown-500 max-w-md text-center">
            Le client n'a pas encore depose d'elements design pour ce projet.
            Les elements apparaitront ici lorsque le client utilisera son espace design.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary header */}
      <Card padding="sm">
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
              <Palette className="w-5 h-5 text-mint-700" />
            </div>
            <div>
              <p className="font-semibold text-brown-900">Espace Design Client</p>
              <p className="text-xs text-brown-500">
                {totalAssets} {totalAssets === 1 ? 'element depose' : 'elements deposes'} par le client
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block w-32 h-2 rounded-full bg-beige-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-mint-500 transition-all duration-500"
                style={{ width: `${(filledCategories / CATEGORIES.length) * 100}%` }}
              />
            </div>
            <Badge variant="mint">
              {filledCategories}/{CATEGORIES.length} categories
            </Badge>
          </div>
        </div>
      </Card>

      {/* Category navigation */}
      <CategoryNav
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        assetCounts={assetCounts ?? {}}
      />

      {/* Category content */}
      {hasAssetsInCategory ? (
        <div className="space-y-8">
          <ImageGallery assets={categoryAssets.imageAssets} />
          <TextEntries assets={categoryAssets.textAssets} />
        </div>
      ) : (
        <EmptyCategory category={activeCategoryConfig} />
      )}
    </div>
  )
}
